import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Key,
  Clock,
  Star,
  MessageSquare,
  Shirt,
  Sparkles,
  ArrowRight,
  Award
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const VacationRentalCleaningPage = () => {
  const whyChooseUs = [
    {
      icon: Award,
      title: "Heather Knows the Airbnb Game",
      description: "Heather isn't just an expert cleaner with 15+ years of executive housekeeping experience—she's also hosted Airbnb guests herself. She understands exactly what guests notice, what gets mentioned in reviews, and what property owners care about most."
    },
    {
      icon: Clock,
      title: "Fast, Reliable Turnovers",
      description: "Same-day turnovers? We do them regularly. When a guest checks out at 11am and the next one arrives at 3pm, every minute counts. We arrive on time, work efficiently, and communicate throughout the process."
    },
    {
      icon: MessageSquare,
      title: "Easy Communication",
      description: "Bob and Heather are directly reachable—no phone trees, no delays. Text us a last-minute change, and we'll handle it. As a small, locally-owned business, we're responsive and flexible in ways larger companies can't match."
    },
    {
      icon: Star,
      title: "Guest-Ready Every Time",
      description: "Your reviews depend on cleanliness. We treat every turnover like it's our own property because we know how important those 5-star ratings are to your success and bookings."
    }
  ];

  const whatWeCover = [
    {
      area: "Living & Sleeping Areas",
      tasks: [
        "All surfaces dusted and wiped",
        "Floors vacuumed and mopped",
        "Furniture straightened and staged",
        "Linens changed and beds made hotel-style",
        "Throw pillows fluffed and arranged"
      ]
    },
    {
      area: "Kitchen",
      tasks: [
        "All dishes washed and put away",
        "Counters and appliances sanitized",
        "Sink and faucet polished",
        "Coffee maker cleaned and ready",
        "Trash removed, fresh liners"
      ]
    },
    {
      area: "Bathrooms",
      tasks: [
        "Toilet, shower, and tub scrubbed",
        "Mirrors streak-free",
        "Fresh towels staged",
        "Floors mopped and sanitized",
        "Toiletries and amenities restocked (if provided)"
      ]
    },
    {
      area: "Guest Experience Details",
      tasks: [
        "Welcome touches arranged (if requested)",
        "Temperature and lighting checked",
        "Final walkthrough for quality",
        "Photo documentation (if requested)",
        "Any special requests handled"
      ]
    }
  ];

  const faqs = [
    {
      question: "Can you handle same-day turnovers?",
      answer: "Yes! We do same-day turnovers regularly for Airbnb and VRBO properties. Just give us as much notice as possible, and we'll coordinate timing to meet your check-in schedule."
    },
    {
      question: "Do you provide linens and towels?",
      answer: "We can work with whatever system you have. Some property owners provide linens on-site that we change and launder. Others handle laundry separately. We're flexible and happy to coordinate with your linen service if you use one."
    },
    {
      question: "What if a guest leaves the property in bad condition?",
      answer: "It happens. If a turnover requires extra time due to excessive mess or damage, we'll communicate immediately and provide an updated timeline and cost. Our goal is always to get your property guest-ready—even when surprises arise."
    },
    {
      question: "How do you handle keys or lockbox codes?",
      answer: "However works best for you. Many clients provide lockbox codes or smart lock access. We're fully insured and trusted by property owners and managers throughout Brevard County's beachside communities."
    },
    {
      question: "Can you restock supplies like toiletries or coffee?",
      answer: "Absolutely. If you keep supplies on-site or want us to pick items up, we can handle restocking as part of the turnover. Just let us know your preferences, and we'll make it happen."
    }
  ];

  // Structured Data
  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Vacation Rental Cleaning | Airbnb & VRBO Turnover | Pierce Premier",
      description: "Professional Airbnb and VRBO cleaning in Brevard County. Fast turnovers, 5-star guest-ready results. Heather is an experienced Airbnb host herself. Serving beachside communities.",
      url: "/services/vacation-rental-cleaning"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Services", url: "/services" },
      { name: "Vacation Rental Cleaning" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "Vacation Rental Cleaning Service",
      description: "Professional Airbnb, VRBO, and vacation rental turnover cleaning. Same-day service available. Serving property owners and managers in Brevard County beach communities."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="Vacation Rental Cleaning | Airbnb & VRBO | Pierce Premier | Brevard County"
        description="Airbnb & VRBO cleaning in Brevard County. Same-day turnovers. Heather is an experienced Airbnb host + 15 years executive housekeeping. 5-star guest-ready service. Call 321-408-1347."
        canonical="/services/vacation-rental-cleaning"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-teal-600 text-white mb-4">Premium Turnover Service</Badge>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Vacation Rental & Airbnb Cleaning
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Your reviews depend on cleanliness. At Pierce Premier, we don&apos;t just clean vacation rentals—we prepare guest experiences. Heather brings 15+ years of executive housekeeping expertise <span className="font-semibold">plus her own experience as an Airbnb host</span>, so she knows exactly what matters to guests and property owners.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  From beachside condos in Cocoa Beach to riverfront homes in Merritt Island, we deliver fast, reliable, guest-ready turnovers that protect your 5-star reputation.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
                  >
                    Get Turnover Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-teal-600 text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-teal-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=800&q=80" 
                  alt="Professional Airbnb and vacation rental cleaning in Brevard County"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border-4 border-teal-100 hidden lg:block">
                  <div className="flex items-center gap-3">
                    <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
                    <div>
                      <div className="font-bold text-lg text-gray-900">5-Star</div>
                      <div className="text-sm text-gray-600">Guest Ready</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Pierce Premier */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Brevard Vacation Rental Owners Choose Pierce Premier
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                We understand the short-term rental business because Heather has lived it as both a professional cleaner and an Airbnb host.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {whyChooseUs.map((reason, idx) => {
                const Icon = reason.icon;
                return (
                  <Card key={idx} className="border-2">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{reason.title}</h3>
                          <p className="text-gray-600 leading-relaxed">{reason.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* What We Cover */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                What&apos;s Included in Every Turnover?
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                A complete, guest-ready clean that protects your reviews and bookings.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {whatWeCover.map((section, idx) => (
                <Card key={idx} className="border-2 hover:border-teal-200 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{section.area}</h3>
                    </div>
                    <ul className="space-y-3">
                      {section.tasks.map((task, taskIdx) => (
                        <li key={taskIdx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{task}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Services for Property Owners & Managers */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Serving Airbnb Hosts & Property Managers
              </h2>
              <p className="text-base sm:text-lg text-gray-600">
                Whether you manage one rental or an entire portfolio, we scale with you.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <Card className="border-2">
                <CardContent className="p-8">
                  <Key className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Individual Property Owners</h3>
                  <p className="text-gray-600 leading-relaxed">
                    You list your beach condo or vacation home on Airbnb or VRBO. We handle turnovers with the care and attention you&apos;d give it yourself—because Heather knows what it&apos;s like to host guests and protect your property&apos;s reputation.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <Shirt className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Property Management Companies</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Managing multiple properties across Brevard County? We offer reliable, consistent turnover service with direct communication. Many property managers trust Pierce Premier because Bob and Heather are easy to reach and deliver every time.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 p-6 bg-teal-50 rounded-xl border-2 border-teal-100">
              <h4 className="font-bold text-gray-900 mb-2">Optional Add-Ons:</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>Linen and towel laundering (if provided on-site)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>Restocking toiletries, coffee, and guest amenities</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>Photo documentation for quality assurance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>Damage or maintenance issue reporting</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Beachside Focus */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-50 to-blue-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Specializing in Brevard&apos;s Beach Communities
            </h2>
            <p className="text-gray-700 mb-8 leading-relaxed">
              Many of our vacation rental clients are located in Brevard&apos;s beachside areas—Cocoa Beach, Satellite Beach, Indian Harbour Beach, Indialantic, and Melbourne Beach. We understand the unique challenges of coastal properties: sand management, salt air buildup, and the fast turnaround expectations of tourist season.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Cocoa Beach", "Satellite Beach", "Indian Harbour Beach", 
                "Indialantic", "Melbourne Beach", "Merritt Island", "Cape Canaveral"
              ].map((city, idx) => (
                <Badge key={idx} variant="outline" className="text-sm py-2 px-4 border-teal-600 text-teal-700">
                  {city}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions About Vacation Rental Cleaning
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-4 border-teal-200 shadow-xl">
              <CardContent className="p-8 md:p-12 text-center">
                <Star className="w-12 h-12 text-yellow-500 fill-yellow-500 mx-auto mb-4" />
                <blockquote className="text-xl md:text-2xl text-gray-700 font-medium mb-6 italic">
                  &quot;Our vacation rental always looks perfect thanks to Pierce Premier. Guests constantly compliment the cleanliness!&quot;
                </blockquote>
                <p className="text-gray-600 font-semibold">Jennifer Martinez</p>
                <p className="text-gray-500 text-sm">Palm Bay, FL - Airbnb Host</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Ready to Protect Your 5-Star Reviews?"
          description="Let Pierce Premier handle your vacation rental turnovers. Fast, reliable, and guest-ready every time."
          primaryButtonText="Get Turnover Quote"
        />
      </div>
    </>
  );
};

export default VacationRentalCleaningPage;
